import React, { forwardRef, useCallback, useRef, type TextareaHTMLAttributes } from "react";
import clsx from "clsx";

import { useSyncConstraintValidationMessage } from "../../../hooks/use-sync-constraint-validation-message.ts";

import styles from "./text-area.module.css";

export type TextAreaLabelLayout = "stacked" | "inline";

export type TextAreaProps = Omit<
  TextareaHTMLAttributes<HTMLTextAreaElement>,
  "id" | "label"
> & {
  label?: string;
  id: string;
  required?: boolean;
  hint?: string;
  /** Shown under the field and mirrored into `setCustomValidity` so `reportValidity()` shows the native browser tooltip. */
  error?: string;
  /** `stacked` = label above field (default). `inline` = label left, field right (one row). */
  labelLayout?: TextAreaLabelLayout;
  /** When true and `maxLength` is set, shows `{length}/{maxLength}` on the right below the field. */
  showCharacterCount?: boolean;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  function TextArea(
    {
      label,
      id,
      required = false,
      hint,
      error,
      labelLayout = "stacked",
      showCharacterCount = false,
      className,
      disabled,
      readOnly,
      rows = 4,
      value,
      maxLength,
      ...rest
    },
    ref
  ) {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const setTextareaRef = useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref != null) {
          (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
        }
      },
      [ref],
    );

    useSyncConstraintValidationMessage(textareaRef, error);

    const hintId = `${id}-hint`;
    const errorId = `${id}-error`;
    const counterId = `${id}-counter`;
    const hasError = error != null && error !== "";
    const hasHint = hint != null && hint !== "";
    const maxLengthNumber =
      typeof maxLength === "number" ? maxLength : undefined;
    const showCount =
      showCharacterCount && maxLengthNumber != null;
    const characterCount = String(value ?? "").length;

    const describedByIds = [
      hasError ? errorId : hasHint ? hintId : null,
      showCount ? counterId : null,
    ].filter((part): part is string => part != null);
    const describedBy =
      describedByIds.length > 0 ? describedByIds.join(" ") : undefined;

    const inline = labelLayout === "inline";

    return (
      <div className={clsx(styles.wrapper, inline && styles.wrapperInline)}>
        {label ? (
          <label
            className={clsx("sub-heading-md", styles.label, inline && styles.labelInline)}
            htmlFor={id}
          >
            {label}
            {required ? (
              <span className="text-danger text-nowrap" aria-hidden>
                {"\u00A0*"}
              </span>
            ) : null}
          </label>
        ) : null}
        <div
          className={clsx(
            inline ? styles.control : styles.controlPassThrough,
          )}
        >
          <textarea
            ref={setTextareaRef}
            id={id}
            rows={rows}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            value={value}
            maxLength={maxLength}
            className={clsx(styles.textarea, "body-sm", className)}
            aria-invalid={hasError ? true : undefined}
            aria-describedby={describedBy}
            {...rest}
          />
          {showCount ? (
            <div className={styles.footer}>
              <div className={styles.footerStart}>
                {hasError ? (
                  <p
                    id={errorId}
                    className={clsx(styles.message, styles.messageError)}
                    role="alert"
                  >
                    {error}
                  </p>
                ) : hasHint ? (
                  <p
                    id={hintId}
                    className={clsx(styles.message, styles.messageHint)}
                  >
                    {hint}
                  </p>
                ) : null}
              </div>
              <span
                id={counterId}
                className={styles.characterCount}
                aria-live="polite"
              >
                {characterCount}/{maxLengthNumber}
              </span>
            </div>
          ) : hasError ? (
            <p
              id={errorId}
              className={clsx(styles.message, styles.messageError)}
              role="alert"
            >
              {error}
            </p>
          ) : hasHint ? (
            <p
              id={hintId}
              className={clsx(styles.message, styles.messageHint)}
            >
              {hint}
            </p>
          ) : null}
        </div>
      </div>
    );
  }
);

TextArea.displayName = "TextArea";
